/**
 * OriginationCausalFractal — 2e fractale d'Origination (V23.16.14)
 *
 * Vue verticale linéaire qui répond à : "qu'est-ce qui rend matériellement
 * possible l'existence et l'activité de mon organisation ?"
 *
 * Diffère du "Mode Marché" (fractale sectorielle 5 niveaux Micro→Macro) en ce
 * que cette fractale remonte en DÉPENDANCE CAUSALE réelle, niveau par niveau,
 * avec profondeur variable (déterminée par les vraies arêtes du graphe).
 *
 * Structure d'affichage : une carte par acteur, empilée verticalement, avec :
 *   - Label humain (nom réel, pas "Support²")
 *   - Type d'acteur + relation causale ("est régulé par", "est fourni par"…)
 *   - 4 réponses structurelles Doctrine 51 :
 *       · Goulot d'étranglement
 *       · Valeur brute vs nette (Score Friction si calculable)
 *       · Substituabilité
 *       · Effet multiplicateur
 *
 * Mode strictement additif : ne remplace ni la fractale sectorielle Marché,
 * ni le graphe multi-niveaux Doctrine 51 (cette vue lit le même graphe).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import PitchCaptureButton from "@/components/PitchCaptureButton";
import {
  Loader2, ArrowDown, Building2, ShieldAlert, Truck, Cpu, Users,
  Handshake, RefreshCcw, HelpCircle, Crown, Layers, ChevronDown, ChevronUp,
  Sparkles, Network, List as ListIcon, GitBranch,
  Zap, Package, Cable,
} from "lucide-react";

const TYPE_META = {
  organisation: { color: "#EF9F27", label: "Organisation", Icon: Building2 },
  regulateur:   { color: "#E24B4A", label: "Régulateur",   Icon: ShieldAlert },
  fournisseur:  { color: "#7F77DD", label: "Fournisseur",  Icon: Truck },
  tech:         { color: "#7F77DD", label: "Tech",         Icon: Cpu },
  client:       { color: "#1D9E75", label: "Client",       Icon: Users },
  partenaire:   { color: "#378ADD", label: "Partenaire",   Icon: Handshake },
  substitut:    { color: "#A1A1AA", label: "Substitut",    Icon: RefreshCcw },
  // V23.15.29 — Substrat matériel (l'infiniment petit)
  energie:         { color: "#FFB627", label: "Énergie",           Icon: Zap,     substrate: true },
  matiere:         { color: "#B08D57", label: "Matière première",  Icon: Package, substrate: true },
  infra_physique:  { color: "#8FA3B0", label: "Infra physique",    Icon: Cable,   substrate: true },
  other:        { color: "#71717A", label: "Autre",        Icon: HelpCircle },
};

const typeMeta = (t) => TYPE_META[(t || "").toLowerCase()] || TYPE_META.other;

function StructuralAnswerCard({ label, answer, accent = "#7F77DD" }) {
  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-md p-2.5">
      <div className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: accent }}>
        {label}
      </div>
      <div className="text-[11px] text-zinc-300 mt-1 leading-snug">{answer || "—"}</div>
    </div>
  );
}

// V23.15.42 — Substrate narratives : explicite ce que chaque brique matérielle
// signifie doctrinalement (« si l'électricité s'arrête, ta banque ne peut plus… »).
const SUBSTRATE_NARRATIVES = {
  energie: {
    title: "L'électricité et l'énergie primaire",
    what: "Sans énergie, aucun serveur ne tourne, aucune ligne bancaire n'est active, aucun paiement ne passe. C'est la couche invisible qui fait tenir tout le reste.",
    if_fails: "Si le réseau électrique s'effondre localement, ton datacenter tombe, tes clients ne peuvent plus opérer, tes revenus s'interrompent immédiatement.",
    doctrine: "Doctrine 51 · dépendance énergétique · substituabilité faible",
    action: "Cartographie tes fournisseurs d'énergie critiques et lance une analyse Bras Armé pour identifier des sources de secours (batteries, groupes électrogènes, contrats duaux).",
  },
  matiere: {
    title: "La matière première physique",
    what: "Les composants, les métaux rares, les serveurs, le papier, les alliages — tout ce qui doit être physiquement produit et transporté pour que ton métier existe.",
    if_fails: "Une rupture de matière première (cuivre, silicium, terres rares) casse tes chaînes d'approvisionnement en cascade — de 3 à 18 mois de perturbation typiquement.",
    doctrine: "Doctrine 51 · dépendance matérielle · bottleneck géopolitique",
    action: "Identifie tes 3 matières les plus critiques et cartographie leurs sources géographiques. Sans plan B, tu es exposé à une rupture systémique.",
  },
  infra_physique: {
    title: "L'infrastructure physique",
    what: "Datacenters, câbles sous-marins, antennes, réseaux ferroviaires, routes — les artères matérielles qui portent tes données, tes gens et tes flux.",
    if_fails: "Une coupure de câble sous-marin ou de datacenter régional te met hors ligne pour plusieurs heures à plusieurs jours. Tes clients migrent chez le concurrent qui est resté debout.",
    doctrine: "Doctrine 51 · dépendance infra · résilience territoriale",
    action: "Vérifie la redondance géographique de tes infras critiques. Une seule zone = une seule panne suffit pour arrêter ton business.",
  },
};

function SubstrateExplainer({ nodeType, downstreamCount }) {
  const key = (nodeType || "").toLowerCase();
  const n = SUBSTRATE_NARRATIVES[key];
  if (!n) return null;
  const meta = TYPE_META[key] || TYPE_META.other;
  return (
    <div
      className="mt-2 rounded-md border p-3 space-y-2"
      style={{
        background: `linear-gradient(135deg, ${meta.color}12 0%, transparent 100%)`,
        borderColor: `${meta.color}40`,
      }}
      data-testid={`substrate-explainer-${key}`}
    >
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: meta.color }}>
          Substrat · lecture doctrinale
        </span>
        <span className="text-[9px] italic text-zinc-500">{n.doctrine}</span>
      </div>
      <div className="text-sm font-semibold text-white">{n.title}</div>
      <div className="text-[12px] text-zinc-300 leading-relaxed">{n.what}</div>
      <div className="text-[12px] text-zinc-200 leading-relaxed border-l-2 pl-2" style={{ borderColor: meta.color }}>
        <strong style={{ color: meta.color }}>Si ça tombe :</strong> {n.if_fails}
      </div>
      {downstreamCount > 0 && (
        <div className="text-[11px] text-zinc-400">
          <strong className="text-white">{downstreamCount}</strong> nœud{downstreamCount > 1 ? "s" : ""} business dépend{downstreamCount === 1 ? "" : "ent"} directement de ce substrat.
        </div>
      )}
      <div className="text-[11px] text-zinc-400 italic">
        <span className="text-[#FFB627]">→ Que faire :</span> {n.action}
      </div>
    </div>
  );
}

function ActorCard({ node, isStart, depth }) {
  const meta = typeMeta(node.type);
  const Icon = meta.Icon;
  const [expanded, setExpanded] = useState(isStart || depth <= 1);
  const isSubstrate = !!meta.substrate;

  return (
    <div
      className="noam-card overflow-hidden"
      style={{
        borderLeft: `4px solid ${isStart ? "#EF9F27" : meta.color}`,
        // Léger dégradé "minéral" quand on est dans le substrat matériel
        ...(isSubstrate && {
          background: `linear-gradient(135deg, ${meta.color}0A 0%, transparent 60%)`,
        }),
      }}
      data-testid={`causal-actor-${node.id}`}
    >
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full p-3 flex items-start gap-3 text-left hover:bg-white/[0.02]"
      >
        <div
          className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
          style={{ background: `${meta.color}22`, border: `1px solid ${meta.color}66` }}
        >
          <Icon className="w-4 h-4" style={{ color: meta.color }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            {isStart && <Crown className="w-3.5 h-3.5 text-[#EF9F27]" />}
            <span className="text-sm font-semibold text-white">{node.name}</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-500">{meta.label}</span>
            {isSubstrate && (
              <span
                className="text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded font-bold"
                style={{ background: `${meta.color}25`, color: meta.color, border: `1px solid ${meta.color}55` }}
                title="Couche physique / énergétique / matière première"
              >
                Substrat
              </span>
            )}
          </div>
          {!isStart && node.via_label && (
            <div className="text-[10px] text-zinc-500 mt-0.5">
              <span className="text-zinc-600">L&apos;acteur précédent</span> {node.via_label} <span className="text-zinc-300">{node.name}</span>
            </div>
          )}
          {node.role && <div className="text-[11px] text-zinc-400 mt-1 truncate">{node.role}</div>}
        </div>
        <div className="text-zinc-500 shrink-0">
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-1.5 border-t border-white/5 pt-2.5">
          {node.description && (
            <div className="text-[11px] text-zinc-400 italic">{node.description}</div>
          )}
          {/* V23.15.42 — Substrate explainer : rend chaque brique parlante */}
          {isSubstrate && (
            <SubstrateExplainer nodeType={node.type} downstreamCount={node.downstream_count || 0} />
          )}
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            <StructuralAnswerCard label="Goulot d&apos;étranglement" answer={node.structural_answers?.goulot} accent="#E24B4A" />
            <StructuralAnswerCard label="Valeur brute → nette" answer={node.structural_answers?.valeur_brute_nette} accent="#1D9E75" />
            <StructuralAnswerCard label="Substituabilité" answer={node.structural_answers?.substituabilite} accent="#EF9F27" />
            <StructuralAnswerCard label="Effet multiplicateur" answer={node.structural_answers?.multiplicateur} accent="#7F77DD" />
          </div>
        </div>
      )}
    </div>
  );
}

export default function OriginationCausalFractal() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [chaining, setChaining] = useState(false);
  const [chainProgress, setChainProgress] = useState({ step: 0, message: "" });
  const [showTree, setShowTree] = useState(false);
  // V23.15.29 — Défaut à 6 pour rendre visible le potentiel jusqu'au substrat
  const [maxDepth, setMaxDepth] = useState(6);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspace/graph/causal-origination?max_depth=${maxDepth}`);
      setData(r.data);
    } catch (e) {
      notify.error(fmtError(e, "Chargement impossible."));
    } finally {
      setLoading(false);
    }
  };

  const enrich = async () => {
    setEnriching(true);
    try {
      const r = await api.post("/workspace/graph/causal-origination/enrich", { target_depth: maxDepth });
      if (r.data.nodes_created > 0) {
        notify.success(r.data.message || "Chaîne enrichie.");
        await load();
      } else {
        notify.info(r.data.message || "Aucun acteur supplémentaire identifié.");
      }
    } catch (e) {
      notify.error(fmtError(e, "Enrichissement impossible."));
    } finally {
      setEnriching(false);
    }
  };

  // V23.15.29 — Chaînage automatique : enrichit en boucle jusqu'à atteindre
  // le substrat matériel (energie/matiere/infra_physique) OU jusqu'à target_depth,
  // OU jusqu'à ce que la chaîne cesse de grandir (safety). Feedback progress en direct.
  const drillToSubstrate = async () => {
    setChaining(true);
    setChainProgress({ step: 0, message: "Initialisation…" });
    const MAX_STEPS = 8;
    const targetDepth = 6;
    let lastDepth = -1;
    let stalledCount = 0;
    try {
      for (let step = 1; step <= MAX_STEPS; step += 1) {
        setChainProgress({ step, message: `Palier ${step} — NOAM explore la chaîne causale…` });
        const r = await api.post("/workspace/graph/causal-origination/enrich", { target_depth: targetDepth });
        const d = r.data || {};
        // Recharge pour visualiser en direct
        await load();
        if (d.substrate_reached) {
          setChainProgress({ step, message: "Substrat matériel atteint ⚡" });
          notify.success("Descente terminée — substrat matériel atteint (énergie / matière / infra physique).");
          break;
        }
        if (!d.has_more_leaves) {
          setChainProgress({ step, message: "Chaîne épuisée." });
          notify.info(`Chaîne épuisée à la profondeur ${d.max_depth_reached}. Aucun substrat supplémentaire détecté.`);
          break;
        }
        if (d.max_depth_reached === lastDepth) {
          stalledCount += 1;
          if (stalledCount >= 2) {
            setChainProgress({ step, message: "Progression stoppée." });
            notify.info(`Descente arrêtée à la profondeur ${d.max_depth_reached} — plus de nouvelles feuilles à enrichir.`);
            break;
          }
        } else {
          stalledCount = 0;
        }
        lastDepth = d.max_depth_reached;
        // Petite pause visuelle pour laisser l'UI respirer
        // eslint-disable-next-line no-await-in-loop
        await new Promise((res) => setTimeout(res, 500));
      }
    } catch (e) {
      notify.error(fmtError(e, "Chaînage interrompu."));
    } finally {
      setChaining(false);
      setTimeout(() => setChainProgress({ step: 0, message: "" }), 3000);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [maxDepth]);

  if (loading && !data) {
    return <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-zinc-500 mx-auto" /></div>;
  }

  if (data?.needs_graph) {
    return (
      <div className="noam-card p-8 text-center" data-testid="causal-fractal-no-graph">
        <Layers className="w-8 h-8 text-[#7F77DD] mx-auto opacity-60" />
        <div className="noam-display text-lg mt-3">Aucun graphe Doctrine 51</div>
        <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
          La fractale d&apos;origination causale est une vue sur votre graphe multi-niveaux.
          Générez d&apos;abord votre graphe Doctrine 51 pour faire apparaître la chaîne causale.
        </p>
        <a
          href="/app/cartographies/graphe"
          className="btn-primary mt-4 inline-flex items-center gap-2"
          data-testid="causal-fractal-go-graph"
        >
          <Layers className="w-3.5 h-3.5" /> Aller au Graphe multi-niveaux
        </a>
      </div>
    );
  }

  const levels = data?.levels || [];
  return (
    <div className="space-y-3" data-testid="causal-fractal-root" id="causal-fractal-capture">
      <div className="noam-card p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="noam-overline flex items-center gap-1.5 text-[#EF9F27]">
              <Layers className="w-3 h-3" /> Mode Origination — Fractale causale
            </div>
            <div className="noam-display text-base font-semibold mt-1">
              Qu&apos;est-ce qui rend matériellement possible l&apos;existence de votre organisation ?
            </div>
            <p className="text-[11px] text-zinc-400 mt-1 max-w-2xl">
              Remontée niveau par niveau dans la chaîne de dépendance réelle — du business (niveaux 1-3),
              en passant par la transition (fournisseurs d&apos;énergie, cloud, infra), jusqu&apos;au{" "}
              <span className="text-[#FFB627] font-semibold">substrat physique</span> (énergie primaire,
              matières premières, câbles sous-marins, satellites) aux niveaux 6-8.
            </p>
            {/* V23.15.29 — Indicateur de profondeur réellement atteinte */}
            {levels.length > 0 && (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">Profondeur atteinte</span>
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: 8 }).map((_, i) => {
                    const reached = levels.some((L) => L.depth >= i);
                    const isSubstrate = i >= 5;
                    return (
                      <div
                        key={i}
                        className="w-3 h-3 rounded-sm border transition-colors"
                        style={{
                          background: reached ? (isSubstrate ? "#FFB627" : "#7F77DD") : "transparent",
                          borderColor: reached ? (isSubstrate ? "#FFB627" : "#7F77DD") : "#3f3f46",
                          opacity: reached ? 1 : 0.35,
                        }}
                        title={`Niveau ${i}${isSubstrate ? " · substrat" : ""}`}
                      />
                    );
                  })}
                </div>
                <span className="text-[10px] text-zinc-500">
                  {levels[levels.length - 1]?.depth ?? 0} / {maxDepth}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <PitchCaptureButton
              targetId="causal-fractal-capture"
              filename="noam-fractale-causale"
              docTitle="Fractale causale — chaîne de dépendance réelle"
              docSubtitle="Ce qui rend matériellement possible l'existence de votre organisation, du business jusqu'au substrat physique."
              threeQEndpoint="/workspace/sources-capital/top-priority"
            />
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">Profondeur</span>
            {[2, 4, 6, 8].map((d) => (
              <button
                key={d}
                onClick={() => setMaxDepth(d)}
                className={`text-[11px] px-2 py-0.5 rounded ${maxDepth === d ? "bg-[#EF9F27]/20 text-[#EF9F27] border border-[#EF9F27]/40" : "text-zinc-400 hover:text-zinc-200 border border-white/10"}`}
                data-testid={`causal-depth-${d}`}
              >
                {d}
              </button>
            ))}
            <div className="w-px h-4 bg-white/10 mx-1" />
            <button
              onClick={() => setShowTree((v) => !v)}
              className={`text-[11px] px-2 py-1 rounded inline-flex items-center gap-1 ${showTree ? "bg-[#7F77DD]/20 text-[#7F77DD] border border-[#7F77DD]/40" : "text-zinc-400 border border-white/10 hover:text-zinc-200"}`}
              data-testid="causal-toggle-tree"
            >
              {showTree ? <ListIcon className="w-3 h-3" /> : <GitBranch className="w-3 h-3" />}
              {showTree ? "Vue liste" : "Vue arbre"}
            </button>
            <button
              onClick={enrich}
              disabled={enriching || chaining}
              className="text-[11px] px-2 py-1 rounded inline-flex items-center gap-1 bg-[#1D9E75]/15 text-[#1D9E75] border border-[#1D9E75]/30 hover:bg-[#1D9E75]/25 disabled:opacity-50"
              data-testid="causal-enrich-btn"
              title="Génère via NOAM les niveaux upstream manquants (1 palier)"
            >
              {enriching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {enriching ? "Enrichissement…" : "Enrichir (1 palier)"}
            </button>
            <button
              onClick={drillToSubstrate}
              disabled={enriching || chaining}
              className="text-[11px] px-2 py-1 rounded inline-flex items-center gap-1 bg-[#FFB627]/15 text-[#FFB627] border border-[#FFB627]/40 hover:bg-[#FFB627]/25 disabled:opacity-50 font-semibold"
              data-testid="causal-drill-substrate-btn"
              title="Enchaîne plusieurs enrichissements jusqu'à atteindre le substrat matériel (énergie, matière, infra physique)"
            >
              {chaining ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
              {chaining ? `Descente… palier ${chainProgress.step}` : "Descendre jusqu'au substrat ⚡"}
            </button>
          </div>
        </div>
        {data?.summary && (
          <div className="text-[11px] text-zinc-500 mt-3 border-t border-white/5 pt-2">
            {data.summary}
          </div>
        )}
        {/* V23.15.29 — Progress banner pour le drill jusqu'au substrat */}
        {chaining && (
          <div className="mt-3 border-t border-[#FFB627]/30 pt-3 flex items-center gap-3" data-testid="causal-drill-progress">
            <div className="w-8 h-8 rounded-full bg-[#FFB627]/20 border border-[#FFB627]/40 flex items-center justify-center shrink-0 relative">
              <Zap className="w-4 h-4 text-[#FFB627]" />
              <div className="absolute inset-0 rounded-full border-2 border-[#FFB627]/40 border-t-[#FFB627] animate-spin" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-[#FFB627] font-semibold">
                Descente en cours vers le substrat matériel
              </div>
              <div className="text-[11px] text-zinc-300 mt-0.5">
                {chainProgress.message || "…"}
              </div>
              <div className="mt-1.5 h-1 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#7F77DD] to-[#FFB627] transition-all duration-500"
                  style={{ width: `${Math.min(100, (chainProgress.step / 8) * 100)}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {levels.length === 0 && (
        <div className="noam-card p-6 text-center text-zinc-500 text-sm">
          Aucun acteur en amont. Enrichissez votre graphe Doctrine 51 pour faire apparaître la chaîne causale.
        </div>
      )}

      {showTree && levels.length > 0 ? (
        <CausalTreeView levels={levels} />
      ) : (
        levels.map((L, idx) => (
        <div key={`level-${L.depth}`}>
          {idx > 0 && (
            <div className="flex items-center justify-center py-1.5">
              <ArrowDown className="w-3.5 h-3.5 text-zinc-600" />
            </div>
          )}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
              {L.depth === 0
                ? "Niveau direct"
                : L.depth <= 2
                  ? `Business · Niveau ${L.depth}`
                  : L.depth <= 4
                    ? `Transition · Niveau ${L.depth}`
                    : `Substrat matériel · Niveau ${L.depth}`}
            </span>
            {L.depth >= 5 && <Zap className="w-3 h-3 text-[#FFB627]" />}
            <span className="text-[10px] text-zinc-600">— {L.nodes.length} acteur(s)</span>
            <div className="flex-1 h-px bg-white/5" />
          </div>
          <div className="space-y-2">
            {L.nodes.map((n) => (
              <ActorCard key={n.id} node={n} isStart={L.depth === 0} depth={L.depth} />
            ))}
          </div>
        </div>
        ))
      )}
    </div>
  );
}

// ============================================================================
// CausalTreeView — Mini-arborescence verticale empilée (vue alternative "wow")
// Affiche la chaîne causale comme un arbre avec connecteurs SVG, plus visuel
// pour un pitch. Réutilise les mêmes données que la vue liste, zéro back-end add.
// ============================================================================
function CausalTreeView({ levels }) {
  // Construit { depth: [{id,name,type,via_label}] } pour rendu compact
  const rows = levels.map((L) => ({
    depth: L.depth,
    label:
      L.depth === 0
        ? "Niveau direct"
        : L.depth <= 2
          ? `Business · Niveau ${L.depth}`
          : L.depth <= 4
            ? `Transition · Niveau ${L.depth}`
            : `Substrat matériel · Niveau ${L.depth}`,
    nodes: L.nodes,
  }));

  return (
    <div className="noam-card p-4" data-testid="causal-tree-view">
      <div className="flex items-center gap-2 mb-3">
        <Network className="w-3.5 h-3.5 text-[#7F77DD]" />
        <span className="noam-overline">Vue arborescente compacte</span>
      </div>
      <div className="space-y-1">
        {rows.map((row, idx) => (
          <div key={`row-${row.depth}`} className="relative">
            {idx > 0 && (
              <div className="absolute left-3 -top-1 h-2 w-px bg-[#7F77DD]/40" />
            )}
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-[#7F77DD]/20 border border-[#7F77DD]/40 flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-[9px] font-bold text-[#7F77DD]">{row.depth}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                  {row.label}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {row.nodes.map((n) => {
                    const meta = typeMeta(n.type);
                    const Icon = meta.Icon;
                    return (
                      <div
                        key={n.id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border text-[11px]"
                        style={{
                          background: `${meta.color}15`,
                          borderColor: `${meta.color}40`,
                          color: meta.color,
                        }}
                        title={`${n.name} — ${n.role || meta.label}`}
                      >
                        <Icon className="w-3 h-3" />
                        <span className="text-zinc-200 max-w-[160px] truncate">{n.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-zinc-500 mt-3 border-t border-white/5 pt-2">
        Bascule sur <strong className="text-zinc-300">Vue liste</strong> pour le détail structurel de chaque acteur.
      </div>
    </div>
  );
}
